import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { Star, Clock, ExternalLink, Navigation, MapPin } from "lucide-react";
import { isUnverifiedPartner } from "../lib/unverifiedPartners";

export const RestaurantCard = ({ restaurant, index = 0 }) => {
  const navigate = useNavigate();
  const { t } = useLang();

  const orderUrl = restaurant.ubereats_link || restaurant.doordash_link || restaurant.order_link;

  const openOrderLink = (e) => {
    e.stopPropagation();
    if (orderUrl) {
      window.open(orderUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/restaurant/${restaurant.id}`);
    }
  };

  const fullAddress = [restaurant.address, restaurant.city]
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i && !(i > 0 && arr[0]?.includes(v)))
    .join(", ");

  const openMap = (e) => {
    e.stopPropagation();
    const q = encodeURIComponent(`${restaurant.name}, ${fullAddress}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank", "noopener,noreferrer");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
      data-testid={`restaurant-card-${restaurant.id}`}
      className="group cursor-pointer bg-white border border-[#E5E0D8] rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-[6px_6px_0px_#2B2B2B] transition-[transform,box-shadow] duration-300"
    >
      <button
        type="button"
        onClick={openOrderLink}
        data-testid={`restaurant-image-order-${restaurant.id}`}
        title={orderUrl ? "Commander en ligne" : "Voir le restaurant"}
        className="relative block w-full aspect-[4/3] overflow-hidden bg-[#F4EFE6] cursor-pointer"
      >
        <img
          src={restaurant.image || "https://images.unsplash.com/photo-1667388968964-4aa652df0a9b?w=800"}
          alt={restaurant.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {restaurant.featured && !isUnverifiedPartner(restaurant.id) && (
          <span className="absolute top-3 left-3 bg-[#E63946] text-white text-xs font-mono font-bold px-3 py-1 rounded-full">
            {t("card.partner")}
          </span>
        )}
        {orderUrl && (
          <span className="absolute bottom-3 right-3 flex items-center gap-1 bg-white/95 text-[#E63946] text-xs font-mono font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            {t("card.order")} <ExternalLink className="w-3 h-3" />
          </span>
        )}
      </button>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-xl font-display font-bold tracking-tight text-[#2B2B2B]">{restaurant.name}</h3>
          <span className="flex items-center gap-1 text-sm font-mono font-bold text-[#4A5D23] shrink-0">
            <Star className="w-4 h-4 fill-[#F4A261] text-[#F4A261]" /> {restaurant.rating}
          </span>
        </div>
        <p className="mt-1 text-sm text-[#5C5C5C] line-clamp-2">{restaurant.description}</p>
        {fullAddress && (
          <button
            type="button"
            onClick={openMap}
            data-testid={`restaurant-address-${restaurant.id}`}
            title="Voir sur Google Maps"
            className="mt-2 flex items-start gap-1.5 text-left text-xs font-mono text-[#5C5C5C] hover:text-[#E63946] transition-colors group/addr"
          >
            <MapPin className="w-3.5 h-3.5 text-[#E63946] shrink-0 mt-0.5" />
            <span className="line-clamp-2 group-hover/addr:underline">{fullAddress}</span>
          </button>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-mono bg-[#F4EFE6] text-[#2B2B2B] px-2 py-1 rounded-full">{restaurant.cuisine}</span>
          <span className="text-xs font-mono bg-[#F4EFE6] text-[#2B2B2B] px-2 py-1 rounded-full">{restaurant.price_range}</span>
          {restaurant.distance_km != null && (
            <span className="flex items-center gap-1 text-xs font-mono bg-[#E63946] text-white px-2 py-1 rounded-full">
              <Navigation className="w-3 h-3" /> {restaurant.distance_km} km
            </span>
          )}
          <span className="ml-auto flex items-center gap-1 text-xs font-mono text-[#5C5C5C]">
            <Clock className="w-3.5 h-3.5" /> {restaurant.delivery_time}
          </span>
        </div>
      </div>
    </motion.div>
  );
};
